/**
 * The Pitch save banner (SE-8389, PV23 · D5; Figma 944:16259, PRD D17).
 *
 * Three moments share one surface: a stale-version rejection (the other
 * editor's version won; the user's change is a copy to re-apply once), three
 * failed tries of one save (Retry / Discard), and a refused re-apply (the
 * copy is gone; the backend's words, nothing retried). The watchlist Pitch
 * and the scenario Pitch both mount it; each passes its own handlers.
 * @module features/scouting/components/molecules/PitchSaveConflictBanner
 */
import React, { memo } from "react";

import type { PitchSaveSurface } from "e2e/testId";

import PitchSaveConflictBannerView from "./PitchSaveConflictBannerView";

/**
 * Which host mounts the banner. The Pitch route keeps the Table mounted
 * (hidden) beside the Pitch, so each host's banner carries its own test
 * identity (TEST_ID_PITCH_SAVE); defaults to the Pitch.
 */
interface PitchSaveSurfaceProp {
  readonly surface?: PitchSaveSurface;
}

/** A stale-version rejection: reload theirs or re-apply the held copy. */
export interface PitchSaveConflictProps extends PitchSaveSurfaceProp {
  readonly variant: "conflict";
  /** Who saved the newer version; "Another editor" when the read has no name. */
  readonly editorName?: string;
  readonly listName: string;
  /** One line naming the user's change, e.g. "A. Ruiz → LB". */
  readonly changeSummary: string;
  readonly onReload: () => void;
  readonly onReapply: () => void;
  /** Both actions wait while the one re-apply is in flight. */
  readonly isBusy?: boolean;
}

/** Three consecutive failures of one save: retry once more, or discard it. */
export interface PitchSaveFailedProps extends PitchSaveSurfaceProp {
  readonly variant: "failed";
  readonly changeSummary: string;
  readonly onRetry: () => void;
  readonly onDiscard: () => void;
  readonly isBusy?: boolean;
}

/** A refused re-apply: the backend's message, and nothing to retry. */
export interface PitchSaveRefusedProps extends PitchSaveSurfaceProp {
  readonly variant: "refused";
  readonly message: string;
  readonly onDismiss: () => void;
}

/** Props for {@link PitchSaveConflictBannerContainer}. */
export type PitchSaveConflictBannerProps =
  | PitchSaveConflictProps
  | PitchSaveFailedProps
  | PitchSaveRefusedProps;

/**
 * Pass-through container: the copy and the buttons are fully determined by
 * the variant, so the View owns the rendering and this stays the seam for
 * telemetry.
 * @param props - One of the three banner moments
 * @returns The rendered banner
 */
const PitchSaveConflictBannerContainer = (
  props: PitchSaveConflictBannerProps
) => <PitchSaveConflictBannerView {...props} />;

PitchSaveConflictBannerContainer.displayName =
  "PitchSaveConflictBannerContainer";
export default memo(PitchSaveConflictBannerContainer);
